import React, { FC, useEffect, useState } from "react";
import { TreatmentKeywordFilter } from "./TreatmentKeywordFilter";
import { TreatmentLocationFilter } from "./TreatmentLocationFilter";
import { TreatmentsContainerLocation } from "./TreatmentsContainerLocation.csharp";
import {
  filterByKeyword,
  filterByLocations,
  filterUnavailableOnLocations,
} from "./servicesContainerFilters";
import { ServicesContainerParams } from "./ServicesContainerParams.csharp";
import { HeadingWithBreakpoints } from "../Heading/HeadingWithBreakpoints";

export const ServicesContainer: FC<ServicesContainerParams> = ({
  heading,
  subheading,
  intro,
  iconUrl,
  labels,
  specializations,
  locations,
}) => {
  const [keyword, setKeyword] = useState("");
  const [selectedLocations, setSelectedLocations] = useState<
    TreatmentsContainerLocation[]
  >([]);
  useEffect(() => {
    const selectedLocations = locations
      .flatMap((l) => [l, ...l.centers])
      .filter((l) => l.isPreselected);

    setSelectedLocations(selectedLocations);
  }, []);

  const getFormattedName = (name: string) => {
    if (keyword && name.toLowerCase().includes(keyword.toLowerCase())) {
      return name.replace(
        new RegExp(keyword, "g"),
        "<strong>" + keyword + "</strong>"
      );
    } else {
      return name;
    }
  };

  const getDangerousSpanForFormattedName = (name: string) => {
    return (
      <span dangerouslySetInnerHTML={{ __html: getFormattedName(name) }} />
    );
  };

  const getUnavailableTreatmentsHeading = () => {
    const flatLocations = locations.flatMap((l) => [l, ...l.centers]);
    const allSelected = flatLocations.length == selectedLocations.length;

    if (allSelected) {
      return "Andre behandlinger";
    } else {
      let names = [];
      for (const loc of selectedLocations) {
        if (
          loc.id == loc.locationId &&
          loc.centers.every((c) => selectedLocations.includes(c))
        ) {
          names.push(loc.name);
        } else if (
          selectedLocations.some((l) => l.id == loc.locationId) == false
        ) {
          names.push(loc.name);
        }
      }
      return labels.unavailableServicesLabel.replace(
        "$locations",
        names.join(", ")
      );
    }
  };

  const onLocationsChange = (newLocations: TreatmentsContainerLocation[]) => {
    setSelectedLocations(newLocations);
  };

  const onKeywordChange = (newKeyword: string) => {
    setKeyword(newKeyword);
  };

  const allLocationsSelected = () => {
    const locationsLength = locations.reduce(
      (prev, current) => prev + current.centers.length + 1,
      0
    );
    return selectedLocations.length == locationsLength;
  };

  const getFilteredTreatments = () => {
    return filterByKeyword(
      filterByLocations(
        specializations,
        selectedLocations,
        allLocationsSelected()
      ),
      keyword
    );
  };

  const getFilteredUnavailableTreatments = () => {
    if (selectedLocations.length > 0 && allLocationsSelected() == false) {
      return filterByKeyword(
        filterUnavailableOnLocations(selectedLocations, specializations),
        keyword
      );
    }
    return [];
  };

  return (
    <>
      <div className="hero theme-inherit mb-4">
        {iconUrl ? (
          <>
            <div className="hero__media theme-inherit-medium d-none d-md-flex justify-content-center align-items-center">
              <img src={iconUrl} alt="" className="hero__media-icon" />
            </div>
          </>
        ) : (
          <div className="hero__media hero__media--no-image theme-inherit-medium" />
        )}

        <div className="hero__text-container">
          <div className="hero__text-container-inner theme-inherit-hero-light">
            <h1 className="hero__heading mb-0">
              {heading && <HeadingWithBreakpoints heading={heading} />}
              {subheading && <span className="fw-normal">{subheading}</span>}
            </h1>
          </div>
        </div>
      </div>

      <div className="container-fluid mt-n3">
        <div className="offset-lg-up">
          <div className="body-text-container mb-3 mb-md-4">
            <div className="lead" dangerouslySetInnerHTML={{ __html: intro }} />
          </div>
        </div>
      </div>

      <div className="container-fluid">
        <div className="offset-lg-up mb-4 mb-lg-5">
          <div className="row mb-3">
            <div className="col-sm-9 col-lg-6">
              <TreatmentKeywordFilter
                onChange={onKeywordChange}
                keyword={keyword}
                placeholder={labels.searchPlaceholder}
              />
            </div>
            <div className="col-sm-9 col-lg-6">
              <TreatmentLocationFilter
                availableLocations={locations}
                selectedLocations={selectedLocations}
                onChange={onLocationsChange}
                selectLocationsLabel={labels.selectLocationsLabel}
                selectAllLabel={labels.allLocationsLabel}
              />
            </div>
          </div>

          <ul className="link-list link-list--bare-bones">
            {getFilteredTreatments().map((treatment) => (
              <li key={treatment.id}>
                <h2 className="h5">
                  <a
                    className="link-list--bare-bones__heading theme-border mb-1"
                    href={treatment.url}
                  >
                    {getDangerousSpanForFormattedName(treatment.name)}
                  </a>
                </h2>
                <ul className="link-list link-list--bare-bones list-columns two-col-sm three-col-md mb-2 mb-md-3 pb-2 pb-md-3 border-bottom border-info">
                  {treatment.subtreatments.map((subtreatment) => (
                    <li key={subtreatment.id}>
                      <a href={subtreatment.url}>
                        {getDangerousSpanForFormattedName(subtreatment.name)}
                      </a>
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>

          {getFilteredUnavailableTreatments().length > 0 && (
            <>
              <hr className="border-bottom border-info border-3 mb-3" />
              <h2 className="h3 mb-3">{getUnavailableTreatmentsHeading()}</h2>
              <ul className="link-list link-list--bare-bones">
                {getFilteredUnavailableTreatments().map((specialization) => (
                  <li key={specialization.id}>
                    <h3 className="h5">
                      <a
                        className="link-list--bare-bones__heading theme-border mb-1"
                        href={specialization.url}
                      >
                        {getDangerousSpanForFormattedName(specialization.name)}
                      </a>
                    </h3>
                    <ul className="link-list link-list--bare-bones list-columns- three-col-md mb-2 mb-md-3 pb-2 pb-md-3 border-bottom border-info">
                      {specialization.subtreatments.map((service) => (
                        <li key={service.id}>
                          <a href={service.url}>
                            {getDangerousSpanForFormattedName(service.name)}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </div>
    </>
  );
};
